
import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useProfile, useUpdateProfile } from "@/services/profileService";
import { useToast } from "@/hooks/use-toast";

interface NotificationContextType {
  pushEnabled: boolean;
  emailEnabled: boolean;
  togglePushNotifications: () => Promise<void>;
  toggleEmailNotifications: () => Promise<void>;
  isLoading: boolean;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const [pushEnabled, setPushEnabled] = useState(false);
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { data: profile } = useProfile();
  const updateProfile = useUpdateProfile();
  const { toast } = useToast();

  useEffect(() => {
    if (profile) {
      setPushEnabled(profile.push_notifications_enabled || false);
      setEmailEnabled(profile.email_notifications_enabled || false);
      setIsLoading(false);
    }
  }, [profile]);

  const togglePushNotifications = async () => {
    if (!profile) return;
    
    try {
      // Check if we need to request permission for push notifications
      if (!pushEnabled && "Notification" in window) {
        const permission = await Notification.requestPermission();
        
        if (permission !== 'granted') {
          toast({
            title: "Notification refusée",
            description: "Vous devez autoriser les notifications dans votre navigateur.",
            variant: "destructive"
          });
          return;
        }
      }

      const newValue = !pushEnabled;
      setPushEnabled(newValue);
      await updateProfile.mutateAsync({
        id: profile.id,
        push_notifications_enabled: newValue
      });
    } catch (error) {
      setPushEnabled(pushEnabled); // Reset to original state if failed
      throw error;
    }
  };

  const toggleEmailNotifications = async () => {
    if (!profile) return;
    
    try {
      const newValue = !emailEnabled;
      setEmailEnabled(newValue);
      await updateProfile.mutateAsync({
        id: profile.id,
        email_notifications_enabled: newValue
      });
    } catch (error) {
      setEmailEnabled(emailEnabled); // Reset to original state if failed
      throw error;
    }
  };

  return (
    <NotificationContext.Provider
      value={{
        pushEnabled,
        emailEnabled,
        togglePushNotifications,
        toggleEmailNotifications,
        isLoading
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error("useNotifications must be used within a NotificationProvider");
  }
  return context;
};
